import React, { Component } from "react";
import { routes, schedules, getBookedSeatsCount } from "../Services";
import Payment from "./Payment";
import { Button, Form, Col, Row, Table } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import moment from "moment";

class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Data states
      allRoutes: [],
      allTerminalOptions: [], // Master list of all terminals
      categoryOptions: [],
      scheduleOptions: [],

      // User selection states
      from: null,
      to: null,
      selectedRoute: null,
      selectedCategory: null,
      transportMode: "",
      date: null,
      time: null,
      qty: 1,

      // UI and Calculation states
      amount: 0,
      transactionCost: 0,
      total: 0,
      availableSeats: undefined,
      showPaymentModal: false,
      isSubmitting: false,
      showErr: false,
      errMsg: "Please fill in all required fields.",

      // User Info
      fullname: "",
      phonenumber: "",
      email: "",
    };
  }

  updateUserFields = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      this.setState({
        fullname: `${user.fname} ${user.lname}`,
        phonenumber: user.phone,
        email: user.email,
      });
    }
  };

  componentDidMount() {
    // Add an event listener for when the user logs in
    window.addEventListener("userLoggedIn", this.updateUserFields);

    // Also run it once on mount in case the user is already logged in
    this.updateUserFields();

    routes()
      .then((allRoutesData) => {
        const uniqueTerminals = new Set();
        allRoutesData.forEach((route) => {
          route.terminals.forEach((terminal) => uniqueTerminals.add(terminal));
        });

        const terminalOptions = Array.from(uniqueTerminals).map((terminal) => ({
          value: terminal,
          label: terminal,
        }));

        this.setState({
          allRoutes: allRoutesData,
          allTerminalOptions: terminalOptions, // Populate the master list
        });
      })
      .catch((err) => console.error("Failed to fetch routes:", err));
  }

  componentWillUnmount() {
    window.removeEventListener("userLoggedIn", this.updateUserFields);
  }

  handleSelectChange = (fieldName) => (selectedOption) => {
    // Logic to prevent selecting the same station
    if (fieldName === "from") {
      if (
        this.state.to &&
        selectedOption &&
        this.state.to.value === selectedOption.value
      ) {
        this.setState({ to: null });
      }
    }
    if (fieldName === "to") {
      if (
        this.state.from &&
        selectedOption &&
        this.state.from.value === selectedOption.value
      ) {
        this.setState({ from: null });
      }
    }

    this.setState({ [fieldName]: selectedOption }, () => {
      // Only find the route if 'from' or 'to' was the field that changed.
      if (fieldName === "from" || fieldName === "to") {
        if (this.state.from && this.state.to) {
          this.findAndSetRoute();
        }
      }

      // These should still run on their respective changes.
      if (fieldName === "selectedCategory") {
        this.calculateFare();
      }
      if (["selectedCategory", "date", "time"].includes(fieldName)) {
        this.updateAvailableSeats();
      }
    });
  };

  findAndSetRoute = () => {
    const { allRoutes, from, to } = this.state;
    if (!from || !to) return;

    const foundRoute = allRoutes.find(
      (route) =>
        route.terminals.includes(from.value) &&
        route.terminals.includes(to.value)
    );

    if (foundRoute) {
      const categoryOptions = foundRoute.fareTable.map((fare) => {
        if (fare.isNegotiable) {
          return {
            value: fare,
            label: `${fare.category} - Negotiable`,
          };
        }
        const price = Number(fare.price) || 0;
        const fee = Math.ceil(0.06 * price);
        const priceWithFee = price + fee;
        return {
          value: fare,
          label: `${fare.category} - SLE ${priceWithFee.toFixed(2)}`,
        };
      });
      this.setState({
        selectedRoute: foundRoute,
        categoryOptions: categoryOptions,
        transportMode: foundRoute.transportMode,
        selectedCategory: null,
        amount: 0,
        total: 0,
        availableSeats: undefined,
      });
      this.fetchSchedules();
    } else {
      this.setState({
        selectedRoute: null,
        categoryOptions: [],
        transportMode: "",
        selectedCategory: null,
        availableSeats: undefined,
      });
    }
  };

  fetchSchedules = () => {
    schedules()
      .then((res) => {
        const selectedDate = this.state.date || moment().format("YYYY-MM-DD");
        const days = [
          "SUNDAY",
          "MONDAY",
          "TUESDAY",
          "WEDNESDAY",
          "THURSDAY",
          "FRIDAY",
          "SATURDAY",
        ];
        const dayName = days[new Date(selectedDate).getDay()];

        const filtered = res.filter(
          (schedule) => schedule.day === dayName && schedule.active
        );

        const scheduleOptions = filtered.flatMap((schedule) => [
          {
            value: schedule.departureTimeGovtWharf,
            label: `Govt Wharf: ${schedule.departureTimeGovtWharf} (${schedule.vessel})`,
            id: schedule._id,
            vessel: schedule.vessel,
          },
          {
            value: schedule.departureTimeTargrin,
            label: `Targrin: ${schedule.departureTimeTargrin} (${schedule.vessel})`,
            id: schedule._id,
            vessel: schedule.vessel,
          },
        ]);
        this.setState({ scheduleOptions });
      })
      .catch((err) => console.error("Failed to fetch schedules:", err));
  };

  updateAvailableSeats = () => {
    const { date, time, selectedCategory } = this.state;

    if (date && time && selectedCategory) {
      const totalSeatsForCategory = selectedCategory.value.seats;
      const vesselName = time.vessel;
      const categoryName = selectedCategory.value.category;

      getBookedSeatsCount(vesselName, categoryName, date, time.value)
        .then((res) => {
          const bookings = res.bookings || 0;
          const availableSeats = totalSeatsForCategory - bookings;
          this.setState({ availableSeats });
        })
        .catch((err) => {
          console.error("Failed to get booked seats count:", err);
          this.setState({ availableSeats: undefined });
        });
    } else {
      this.setState({ availableSeats: undefined });
    }
  };

  calculateFare = () => {
    const { selectedCategory, qty } = this.state;

    if (
      selectedCategory &&
      selectedCategory.value &&
      !selectedCategory.value.isNegotiable
    ) {
      const price = selectedCategory.value.price;
      const amount = price * qty;
      // Calculate transaction cost (6% of amount). need to be round up to the nearest whole number
      const transactionCost = Math.ceil(0.06 * amount);
      const total = amount + transactionCost;
      this.setState({
        amount: amount,
        transactionCost: transactionCost,
        total: total,
      });
    } else {
      this.setState({ amount: 0, transactionCost: 0, total: 0 });
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();
    this.setState({ showErr: false, isSubmitting: true });

    const { from, to, selectedRoute, selectedCategory, date, time, qty } =
      this.state;

    if (
      from &&
      to &&
      selectedRoute &&
      selectedCategory &&
      date &&
      time &&
      qty > 0
    ) {
      if (selectedCategory.value.isNegotiable) {
        this.setState({
          showErr: true,
          errMsg: "Price is negotiable. Please contact us to book.",
        });
        return;
      }
      this.setState({ showPaymentModal: true });
    } else {
      this.setState({
        showErr: true,
        errMsg: "Please fill in all required fields.",
      });
    }
  };

  handleDateChange = (dt) => {
    const date = moment(dt).format("YYYY-MM-DD");
    this.setState({ date }, () => {
      this.fetchSchedules();
      this.updateAvailableSeats();
    });
  };

  render() {
    const {
      from,
      to,
      selectedRoute,
      categoryOptions,
      selectedCategory,
      transportMode,
      date,
      time,
      scheduleOptions,
      qty,
      amount,
      transactionCost,
      total,
      showErr,
      errMsg,
      fullname,
      phonenumber,
      email,
      showPaymentModal,
      availableSeats,
      allTerminalOptions,
    } = this.state;

    // Dynamically filter options to prevent selecting the same station
    const fromOptions = allTerminalOptions.filter(
      (option) => !to || option.value !== to.value
    );
    const toOptions = allTerminalOptions.filter(
      (option) => !from || option.value !== from.value
    );

    const isBookingDisabled =
      availableSeats < qty ||
      (selectedCategory &&
        selectedCategory.value &&
        selectedCategory.value.isNegotiable);
    const asColor = availableSeats < qty ? "red" : "black";

    return (
      <>
        <div className="container mt-4 mb-4">
          <Form onSubmit={this.handleSubmit}>
            <Row>
              {/* --- Left Column: Booking Form --- */}
              <Col md={7}>
                <div className="p-4 border rounded shadow-sm bg-white h-100">
                  <h4>Book Your Trip</h4>
                  <hr />

                  <h5 className="mb-3 mt-4">Passenger Details</h5>
                  <Row>
                    <Form.Group as={Col} md={12} controlId="fullname">
                      <Form.Label>
                        Full Name <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter full name"
                        value={fullname}
                        onChange={(e) =>
                          this.setState({ fullname: e.target.value })
                        }
                        disabled={!!JSON.parse(localStorage.getItem("user"))}
                        required
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} md={6} controlId="phonenumber">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter phone number"
                        value={phonenumber}
                        onChange={(e) =>
                          this.setState({ phonenumber: e.target.value })
                        }
                        disabled={!!JSON.parse(localStorage.getItem("user"))}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6} controlId="email">
                      <Form.Label>
                        Email <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter email"
                        value={email}
                        onChange={(e) =>
                          this.setState({ email: e.target.value })
                        }
                        required
                        disabled={!!JSON.parse(localStorage.getItem("user"))}
                      />
                    </Form.Group>
                  </Row>
                  <hr />

                  <h5 className="mb-3 mt-4">Trip Details</h5>
                  <Row>
                    <Form.Group as={Col} md={6} controlId="from">
                      <Form.Label>From</Form.Label>
                      <Select
                        options={fromOptions}
                        value={from}
                        onChange={this.handleSelectChange("from")}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6} controlId="to">
                      <Form.Label>To</Form.Label>
                      <Select
                        options={toOptions}
                        value={to}
                        onChange={this.handleSelectChange("to")}
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} md={6} controlId="category">
                      <Form.Label>Category</Form.Label>
                      <Select
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={this.handleSelectChange("selectedCategory")}
                        isDisabled={!selectedRoute}
                        placeholder={
                          !selectedRoute
                            ? "Select route first"
                            : "Select a category"
                        }
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6} controlId="transportMode">
                      <Form.Label>Transport Mode</Form.Label>
                      <Form.Control
                        type="text"
                        value={transportMode}
                        disabled
                        placeholder="Auto-selected"
                      />
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} md={6} controlId="date">
                      <Form.Label>Date</Form.Label>
                      <DatePicker
                        className="form-control"
                        onChange={this.handleDateChange}
                        minDate={new Date()}
                        selected={date ? new Date(date) : null}
                        placeholderText="YYYY-MM-DD"
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={6} controlId="time">
                      <Form.Label>Time</Form.Label>
                      <Select
                        options={scheduleOptions}
                        value={time}
                        onChange={this.handleSelectChange("time")}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </Form.Group>
                  </Row>
                </div>
              </Col>

              {/* --- Right Column: Fare Summary --- */}
              <Col md={5}>
                <div className="p-4 border rounded shadow-sm bg-white h-100">
                  <h4>Summary</h4>
                  <hr />
                  {amount > 0 || availableSeats !== undefined ? (
                    <>
                      <div
                        style={{
                          marginBottom: 20,
                          background: "#f8f9fa",
                          borderRadius: 8,
                          padding: "18px 20px",
                          boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                        }}
                      >
                        <h6
                          style={{
                            marginBottom: 18,
                            color: "#0072c6",
                            fontWeight: 700,
                          }}
                        >
                          Ticket Holder Information
                        </h6>
                        <Form.Group style={{ marginBottom: 16 }}>
                          <Form.Label style={{ fontWeight: 500 }}>
                            Full Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={fullname}
                            disabled
                            style={{ height: 40, background: "#f4f6f8" }}
                          />
                        </Form.Group>
                        <div style={{ display: "flex", gap: 18 }}>
                          <Form.Group style={{ flex: 1 }}>
                            <Form.Label style={{ fontWeight: 500 }}>
                              Phone Number
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={phonenumber}
                              disabled
                              style={{ height: 40, background: "#f4f6f8" }}
                            />
                          </Form.Group>
                          <Form.Group style={{ flex: 1 }}>
                            <Form.Label style={{ fontWeight: 500 }}>
                              Email
                            </Form.Label>
                            <Form.Control
                              type="email"
                              value={email}
                              disabled
                              style={{ height: 40, background: "#f4f6f8" }}
                            />
                          </Form.Group>
                        </div>
                      </div>

                      <Table borderless size="sm" className="mt-3">
                        <tbody>
                          {availableSeats !== undefined && (
                            <tr>
                              <td>Available Seats</td>
                              <td
                                align="right"
                                style={{ color: asColor, fontWeight: "bold" }}
                              >
                                {availableSeats}
                              </td>
                            </tr>
                          )}
                          {amount > 0 && (
                            <>
                              <tr>
                                <td>No. of Tickets</td>
                                <td align="right">{qty}</td>
                              </tr>
                              <tr>
                                <td>Amount</td>
                                <td align="right">SLE {total.toFixed(2)}</td>
                              </tr>
                              {/* Transaction Cost hidden by request; total still includes it */}
                              <tr
                                style={{
                                  fontWeight: "bold",
                                  borderTop: "1px solid #dee2e6",
                                }}
                              >
                                <td className="pt-2">Total</td>
                                <td align="right" className="pt-2">
                                  SLE {total.toFixed(2)}
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </Table>
                    </>
                  ) : (
                    <p className="text-muted mt-4">
                      Please complete the form to see your fare summary.
                    </p>
                  )}

                  {showErr && <p className="text-danger mt-3">{errMsg}</p>}

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isBookingDisabled}
                    className="w-100 mt-4"
                  >
                    {selectedCategory &&
                    selectedCategory.value &&
                    selectedCategory.value.isNegotiable
                      ? "Contact to Book"
                      : "Pay with WanGov"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>

        {showPaymentModal && (
          <Payment
            showModal={showPaymentModal}
            onHide={() => this.setState({ showPaymentModal: false })}
            amount={amount}
            total={total}
            transactionCost={transactionCost.toFixed(2)}
            from={from ? from.value : ""}
            to={to ? to.value : ""}
            train={selectedRoute.transportMode}
            trainClass={selectedCategory.label}
            time={time ? time.value : ""}
            qty={qty}
            date={date}
            onPurchaseComplete={() => this.props.history.push("/reservations")}
            email={email}
            phoneNo={phonenumber}
            fullname={fullname}
          />
        )}
      </>
    );
  }
}

export default Home;
